export interface ScreenshotOptions {
  maskSelector?: string;
}

const blobToFile = (blob: Blob, name: string): File =>
  new File([blob], name, { type: blob.type });

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
      "image/png",
    );
  });

export const captureDOMScreenshot = async (
  opts: ScreenshotOptions = {},
): Promise<File> => {
  const { default: html2canvas } = await import("html2canvas-pro");
  const maskSelector = opts.maskSelector || "[data-mask]";

  // Hide all <ark-snitch> hosts on the live DOM before capturing — onclone
  // hiding isn't reliable across html2canvas-pro versions when the modal
  // lives in shadow DOM. Restore on finally so an error doesn't leak the
  // hidden state.
  const hosts = Array.from(
    document.querySelectorAll("ark-snitch"),
  ) as HTMLElement[];
  const prevDisplay = hosts.map((h) => h.style.display);
  hosts.forEach((h) => {
    h.style.display = "none";
  });

  // Wait one paint so layout reflects the hidden state before snapshotting.
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      logging: false,
      // Crop to the current viewport so long pages don't produce a giant PNG
      // that becomes unreadable when the annotator/report viewer scales it
      // down to fit.
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll<HTMLElement>(maskSelector).forEach((el) => {
          el.style.filter = "blur(10px)";
          el.setAttribute("data-snitch-masked", "true");
        });
      },
    });
    const blob = await canvasToBlob(canvas);
    return blobToFile(blob, `screenshot-${Date.now()}.png`);
  } finally {
    hosts.forEach((h, i) => {
      h.style.display = prevDisplay[i] ?? "";
    });
  }
};

